/**
 * Settings groups (owner-approved UI clean-up, 2026-10-04): the Settings dialog lists desk
 * settings by what a trader is doing — Connection, Order defaults, Risk limits, Display, Hotkeys —
 * then Security (account sign-in, not desk settings) and, in development builds only, Dev.
 *
 * "Instrument" (default symbol, roll) was part of the approved order but no such setting exists
 * yet, so the group is left out rather than shown empty.
 *
 * Old section ids (the pre-regroup tabs: "risk", "platform", "discipline" …) still open Settings
 * from the palette, the coach banner and saved links: `resolveSettingsTab` maps each to its new
 * group and, where the group has several parts, the part to show.
 *
 * Risk limits use an explicit save (`RiskStage`): edits are staged until "Save risk limits";
 * "Discard" reverts; leaving the group or closing Settings with staged edits asks first. Pure and
 * DOM-free; covered by settingsGroups.test.ts.
 */

export type SettingsGroupId = "connection" | "orders" | "risk" | "display" | "hotkeys" | "security" | "dev";

export type SettingsGroup = {
  id: SettingsGroupId;
  label: string;
  /** One line under the group title: what the group changes. */
  effect: string;
};

/** Every group, in list order. `visibleSettingsGroups` drops the ones this session must not show. */
export const SETTINGS_GROUPS: readonly SettingsGroup[] = [
  {
    id: "connection",
    label: "Connection",
    effect: "Rithmic logins, what Rithmic reports for each account, and this device's chart history.",
  },
  {
    id: "orders",
    label: "Order defaults",
    effect:
      "How orders are confirmed, your quick quantities and your bracket templates. Nothing here sends an order.",
  },
  {
    id: "risk",
    label: "Risk limits",
    effect: "Daily loss, profit stops, contract caps, firm rules and the discipline coach, per account.",
  },
  {
    id: "display",
    label: "Display",
    effect: "Look, layout, clock and time zone, privacy and sounds. Display only: no order is affected.",
  },
  {
    id: "hotkeys",
    label: "Hotkeys",
    effect: "The keys that trade, draw and move around the desk.",
  },
  {
    id: "security",
    label: "Security",
    effect: "How you sign in: password, passkeys, PIN, approved devices and sign-out.",
  },
  {
    id: "dev",
    label: "Dev",
    effect: "Development tools. Not shown in production builds.",
  },
];

/**
 * The groups to list: Dev only in a development build (`import.meta.env.DEV`), never in production.
 * The owner-only Users list is a part of Security, not a group, so `owner` does not change the list.
 */
export function visibleSettingsGroups({ dev }: { dev: boolean }): readonly SettingsGroup[] {
  return SETTINGS_GROUPS.filter((g) => g.id !== "dev" || dev);
}

/** Parts of a group that a link can point at (a section to scroll to, or the Risk limits part). */
export type SettingsSection =
  | "rithmic"
  | "account"
  | "data"
  | "diagnostics"
  | "brackets"
  | "limits"
  | "firm"
  | "coach"
  | "devices"
  | "users";

/** The Risk limits parts, in order (one shows at a time). */
export const RISK_PARTS: readonly (readonly ["limits" | "firm" | "coach", string])[] = [
  ["limits", "Loss & profit limits"],
  ["firm", "Firm rules"],
  ["coach", "Discipline coach"],
];

export type RiskPart = (typeof RISK_PARTS)[number][0];

/** Ids that used to name a Settings tab, and where each lives now. */
export const LEGACY_SETTINGS_TABS: Readonly<
  Record<string, { group: SettingsGroupId; section: SettingsSection | null }>
> = {
  // "Account information" held the Rithmic figures (read-only) and the limits; the palette's
  // "Risk settings (daily max loss …)" opened it, so the id keeps pointing at the limits.
  risk: { group: "risk", section: "limits" },
  rithmic: { group: "connection", section: "rithmic" },
  appearance: { group: "display", section: null },
  platform: { group: "display", section: null },
  brackets: { group: "orders", section: "brackets" },
  discipline: { group: "risk", section: "coach" },
  data: { group: "connection", section: "data" },
  devices: { group: "security", section: "devices" },
  security: { group: "security", section: null },
  users: { group: "security", section: "users" },
};

/** New ids that point at a part of a group. */
const SECTION_IDS: Readonly<Record<string, { group: SettingsGroupId; section: SettingsSection }>> = {
  account: { group: "connection", section: "account" },
  diagnostics: { group: "connection", section: "diagnostics" },
  firm: { group: "risk", section: "firm" },
  coach: { group: "risk", section: "coach" },
  limits: { group: "risk", section: "limits" },
};

export type SettingsTarget = { group: SettingsGroupId; section: SettingsSection | null };

/**
 * Where a "open Settings at …" request lands: a group id opens that group; an old tab id or a
 * section id opens its group at that part. Anything unknown, or a group this build does not show
 * (Dev in production), opens the first group.
 */
export function resolveSettingsTab(id: string, { dev }: { dev: boolean }): SettingsTarget {
  const visible = visibleSettingsGroups({ dev });
  const first: SettingsTarget = { group: visible[0]?.id ?? "connection", section: null };
  const target: SettingsTarget | undefined = Object.hasOwn(LEGACY_SETTINGS_TABS, id)
    ? LEGACY_SETTINGS_TABS[id]
    : Object.hasOwn(SECTION_IDS, id)
      ? SECTION_IDS[id]
      : visible.some((g) => g.id === id)
        ? { group: id as SettingsGroupId, section: null }
        : undefined;
  if (target === undefined || !visible.some((g) => g.id === target.group)) return first;
  return target;
}

// ---- explicit save ---------------------------------------------------------------------------

export type StageResult = { ok: boolean; message: string };

/** One form taking part in the Risk limits save. */
export type StageEntry = {
  /** Shown in the save result ("Firm rules: …"). */
  label: string;
  dirty: boolean;
  /** Commit the staged edits. Resolves `ok: false` (and keeps the edits) when not saved. */
  save: () => Promise<StageResult>;
  /** Drop the staged edits and show the saved values again. */
  discard: () => void;
};

/**
 * The Risk limits group's staged edits: each form registers itself and reports whether it holds
 * unsaved edits. "Save risk limits" commits every form with edits; "Discard" reverts them all; a
 * request to leave (close Settings, another group, another account) asks first while anything is
 * staged. Listeners hear every change to the dirty state.
 */
export class RiskStage {
  private readonly entries = new Map<string, StageEntry>();
  /** Which registration owns each key (a remount's register may run before the old unregister). */
  private readonly owners = new Map<string, symbol>();
  private readonly listeners = new Set<() => void>();
  private saving = false;

  /** Register (or replace) a form; returns its unregister function. */
  register(key: string, entry: StageEntry): () => void {
    const owner = Symbol(key);
    this.owners.set(key, owner);
    this.entries.set(key, entry);
    this.emit();
    return () => {
      if (this.owners.get(key) !== owner) return;
      this.owners.delete(key);
      this.entries.delete(key);
      this.emit();
    };
  }

  /** Update a registered form's dirty flag (and its latest save / discard closures). */
  update(key: string, entry: StageEntry): void {
    if (!this.owners.has(key)) return;
    const before = this.entries.get(key);
    this.entries.set(key, entry);
    if (before?.dirty !== entry.dirty) this.emit();
  }

  /** Any form holds staged edits. */
  get dirty(): boolean {
    for (const e of this.entries.values()) if (e.dirty) return true;
    return false;
  }

  /** Labels of the forms holding staged edits, in registration order. */
  get dirtyLabels(): string[] {
    return [...this.entries.values()].filter((e) => e.dirty).map((e) => e.label);
  }

  get busy(): boolean {
    return this.saving;
  }

  /**
   * Commit every form with staged edits, in order. A form that does not save keeps its edits and
   * its message is reported; the others still save. Nothing staged: nothing is sent.
   */
  async saveAll(): Promise<StageResult> {
    const dirty = [...this.entries.values()].filter((e) => e.dirty);
    if (dirty.length === 0) return { ok: true, message: "No unsaved risk changes." };
    if (this.saving) return { ok: false, message: "Already saving." };
    this.saving = true;
    this.emit();
    const failed: string[] = [];
    try {
      for (const e of dirty) {
        const r = await e.save().catch((err: unknown) => ({ ok: false, message: String(err) }));
        if (!r.ok) failed.push(`${e.label}: ${r.message}`);
      }
    } finally {
      this.saving = false;
      this.emit();
    }
    return failed.length === 0
      ? { ok: true, message: "Risk limits saved." }
      : { ok: false, message: `Not saved — ${failed.join(" ")}` };
  }

  /** Revert every form's staged edits. */
  discardAll(): void {
    for (const e of [...this.entries.values()]) if (e.dirty) e.discard();
  }

  /**
   * A request to leave the staged forms (close Settings, open another group or account): "prompt"
   * while anything is staged — the caller asks "Discard unsaved risk changes?" — else "leave".
   */
  leave(): "leave" | "prompt" {
    return this.dirty ? "prompt" : "leave";
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private emit(): void {
    for (const l of [...this.listeners]) l();
  }
}

/**
 * A staged copy of a document that otherwise saves on every change (the discipline coach): edits
 * go to the draft; `commit` hands the draft to `persist` once; `discard` drops it. `dirty` compares
 * by value, so editing a field back to its saved value is not an unsaved change.
 */
export type Draft<T> = { saved: T; draft: T | null };

export function draftValue<T>(d: Draft<T>): T {
  return d.draft ?? d.saved;
}

export function editDraft<T extends object>(d: Draft<T>, patch: Partial<T>): Draft<T> {
  return { saved: d.saved, draft: { ...draftValue(d), ...patch } };
}

export function draftDirty<T>(d: Draft<T>): boolean {
  return d.draft !== null && JSON.stringify(d.draft) !== JSON.stringify(d.saved);
}

/** Persist the draft (if it differs) and return the committed state. */
export function commitDraft<T>(d: Draft<T>, persist: (v: T) => void): Draft<T> {
  if (d.draft === null) return d;
  if (draftDirty(d)) persist(d.draft);
  return { saved: d.draft, draft: null };
}
